import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "../AdminPanel.css";
import SuperAdminSidebar from "../SuperAdminSidebar";
import ManagerSidebar from "../ManagerSidebar";
import LeaderSidebar from "../LeaderSidebar";
import MemberSidebar from "../MemberSidebar";
import AdminNavbar from './AdminNavbar';

const CreateGroup = () => {
    const navigate = useNavigate();
    const [userType, setUserType] = useState("");
    const [userFullName, setUserFullName] = useState("");
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [selectedMembers, setSelectedMembers] = useState([]);
    const searchInputRef = useRef(null);
    const dropdownRef = useRef(null);

    const [formData, setFormData] = useState({
        group_name: "",
        group_description: "",
        leader_id: ""
    });

    useEffect(() => {
        const storedUserType = localStorage.getItem("usertype");
        const storedFname = localStorage.getItem("fname");
        const storedLname = localStorage.getItem("lname");
        const storedUserId = localStorage.getItem("user_id");
        
        console.log("Stored user_id:", storedUserId); // Debug log
        
        if (!storedUserType) {
            navigate("/login");
        } else {
            setUserType(storedUserType);
            setUserFullName(`${storedFname || ''} ${storedLname || ''}`.trim());
        }
        
        fetchUsers();
        
        // Click outside to close dropdown
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [navigate]);

    const fetchUsers = () => {
        setLoading(true);
        fetch("https://skcapi.btcjapan.co/fetch_users_simple.php")
            .then(response => response.json())
            .then(data => {
                if (data.Status === "200") {
                    setUsers(data.Users);
                }
            })
            .catch(error => console.error("Error fetching users:", error))
            .finally(() => setLoading(false));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle member search
    const handleMemberSearch = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        
        if (value.trim() === "") {
            setFilteredUsers([]);
            setShowDropdown(false);
            return;
        }
        
        const filtered = users.filter(user => {
            const fullName = `${user.fname} ${user.lname}`.toLowerCase();
            const search = value.toLowerCase();
            return fullName.includes(search) || 
                   user.email.toLowerCase().includes(search) ||
                   user.fname.toLowerCase().includes(search) ||
                   user.lname.toLowerCase().includes(search);
        });
        
        // Exclude already selected members
        const availableUsers = filtered.filter(user => 
            !selectedMembers.some(m => m.id === user.id) &&
            user.id !== parseInt(formData.leader_id)
        );
        
        setFilteredUsers(availableUsers);
        setShowDropdown(availableUsers.length > 0);
    };

    // Select a member from dropdown
    const handleSelectMember = (user) => {
        setSelectedMembers(prev => [...prev, user]);
        setSearchTerm("");
        setFilteredUsers([]);
        setShowDropdown(false);
        if (searchInputRef.current) {
            searchInputRef.current.value = "";
        }
    };

    // Remove a selected member
    const handleRemoveMember = (userId) => {
        setSelectedMembers(prev => prev.filter(m => m.id !== userId));
    };

    // Clear all members
    const handleClearMembers = () => {
        if (window.confirm("Clear all selected members?")) {
            setSelectedMembers([]);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setLoading(true);

        const createdBy = localStorage.getItem("user_id");
        console.log("Created by user_id:", createdBy); // Debug log
        
        if (!createdBy) {
            alert("Error: User not logged in properly. Please log out and login again.");
            setLoading(false);
            return;
        }

        const memberIds = selectedMembers.map(m => m.id);
        
        // Add leader to members if not already selected
        if (formData.leader_id && !memberIds.includes(parseInt(formData.leader_id))) {
            memberIds.push(parseInt(formData.leader_id));
        }

        const formDataToSend = new FormData();
        formDataToSend.append("group_name", formData.group_name);
        formDataToSend.append("group_description", formData.group_description || "");
        formDataToSend.append("leader_id", formData.leader_id);
        formDataToSend.append("created_by", createdBy);
        formDataToSend.append("member_ids", JSON.stringify(memberIds));

        console.log("Sending data:", {
            group_name: formData.group_name,
            leader_id: formData.leader_id,
            created_by: createdBy,
            member_ids: memberIds
        }); // Debug log

        fetch("https://skcapi.btcjapan.co/create_group.php", {
            method: "POST",
            body: formDataToSend
        })
        .then(response => response.json())
        .then(data => {
            if (data.Status === "200") {
                alert("Group created successfully!");
                // Reset form
                setFormData({
                    group_name: "",
                    group_description: "",
                    leader_id: ""
                });
                setSelectedMembers([]);
                setSearchTerm("");
                setFilteredUsers([]);
                navigate("/view-groups");
            } else {
                alert("Error: " + data.Message);
                console.error("Error response:", data);
            }
        })
        .catch(error => {
            console.error("Error creating group:", error);
            alert("Network error occurred");
        })
        .finally(() => setLoading(false));
    };

    const logout = () => {
        localStorage.removeItem("token");
        localStorage.removeItem("email");
        localStorage.removeItem("userName");
        localStorage.removeItem("usertype");
        localStorage.removeItem("fname");
        localStorage.removeItem("lname");
        localStorage.removeItem("user_id");
        navigate("/login");
    };

    // Get leader name for display
    const getLeaderName = () => {
        if (!formData.leader_id) return "No leader selected";
        const leader = users.find(u => u.id === parseInt(formData.leader_id));
        return leader ? `${leader.fname} ${leader.lname}` : "Unknown";
    };

    return (
        <div className="admin-container">
            <div className="sidebar-container">
              {userType === "administrator" && <SuperAdminSidebar />}
              {userType === "leader" && <LeaderSidebar />}
              {userType === "manager" && <ManagerSidebar />}
              {userType === "member" && <MemberSidebar />}
            </div>

            <div className="admin-content">
                {/* Use the new AdminNavbar component */}
                <AdminNavbar pageTitle="Create New Group" />

                <div className="main-section">
                    <h3 className="section-title">Create New Group</h3>
                    
                    <form onSubmit={handleSubmit}>
                        <div className="mt-3">
                            <label className="form-label">Group Name <span className="text-danger">*</span></label>
                            <input 
                                type="text" 
                                name="group_name" 
                                className="form-control" 
                                value={formData.group_name} 
                                onChange={handleChange} 
                                required 
                                placeholder="Enter group name"
                            />
                        </div>

                        <div className="mt-3">
                            <label className="form-label">Group Description</label>
                            <textarea 
                                name="group_description" 
                                className="form-control" 
                                value={formData.group_description} 
                                onChange={handleChange} 
                                rows="3"
                                placeholder="Enter group description (optional)"
                            ></textarea>
                        </div>

                        <div className="mt-3">
                            <label className="form-label">Group Leader <span className="text-danger">*</span></label>
                            <select 
                                name="leader_id" 
                                className="form-select" 
                                value={formData.leader_id} 
                                onChange={handleChange} 
                                required
                            >
                                <option value="">Select Leader</option>
                                {users.map(user => (
                                    <option key={user.id} value={user.id}>
                                        {user.fname} {user.lname} ({user.email})
                                    </option>
                                ))}
                            </select>
                            {formData.leader_id && (
                                <small className="text-success">Selected Leader: {getLeaderName()}</small>
                            )}
                        </div>

                        <div className="mt-3" ref={dropdownRef}>
                            <label className="form-label">Search & Add Members</label>
                            <div className="position-relative">
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    className="form-control"
                                    placeholder="Type member name to search..."
                                    onChange={handleMemberSearch}
                                    autoComplete="off"
                                />
                                {showDropdown && filteredUsers.length > 0 && (
                                    <div className="position-absolute w-100 bg-white border rounded shadow-sm" style={{ 
                                        maxHeight: "200px", 
                                        overflowY: "auto", 
                                        zIndex: 1000,
                                        top: "100%",
                                        left: 0
                                    }}>
                                        {filteredUsers.map(user => (
                                            <div
                                                key={user.id}
                                                className="p-2 border-bottom cursor-pointer"
                                                style={{ cursor: "pointer" }}
                                                onClick={() => handleSelectMember(user)}
                                                onMouseEnter={(e) => e.target.style.backgroundColor = "#f0f0f0"}
                                                onMouseLeave={(e) => e.target.style.backgroundColor = "white"}
                                            >
                                                <strong>{user.fname} {user.lname}</strong>
                                                <br />
                                                <small className="text-muted">{user.email} ({user.usertype})</small>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <small className="text-muted">Start typing a name to search and add members</small>
                        </div>

                        <div className="mt-3">
                            <label className="form-label">Selected Members <span className="badge bg-info">{selectedMembers.length}</span></label>
                            {selectedMembers.length > 0 ? (
                                <div className="border rounded p-2" style={{ minHeight: "60px" }}>
                                    <div className="d-flex flex-wrap gap-2">
                                        {selectedMembers.map(member => (
                                            <div key={member.id} className="bg-light border rounded px-2 py-1 d-flex align-items-center">
                                                <span>{member.fname} {member.lname}</span>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-link text-danger p-0 ms-1"
                                                    onClick={() => handleRemoveMember(member.id)}
                                                    style={{ textDecoration: "none", fontSize: "14px" }}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))}
                                        {selectedMembers.length > 1 && (
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-danger"
                                                onClick={handleClearMembers}
                                            >
                                                Clear All
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="border rounded p-2 text-muted text-center">
                                    No members selected yet. Use the search above to add members.
                                </div>
                            )}
                        </div>

                        <div className="mt-3">
                            <div className="card bg-light">
                                <div className="card-body">
                                    <h6>Group Summary</h6>
                                    <p className="mb-1"><strong>Group Name:</strong> {formData.group_name || "Not set"}</p>
                                    <p className="mb-1"><strong>Leader:</strong> {getLeaderName()}</p>
                                    <p className="mb-0"><strong>Total Members:</strong> {selectedMembers.length + (formData.leader_id ? 1 : 0)}</p>
                                </div>
                            </div>
                        </div>

                        <div className="mt-4 text-center">
                            <button type="submit" className="btn btn-primary px-5" disabled={loading}>
                                {loading ? 'Creating...' : 'Create Group'}
                            </button>
                            <button 
                                type="button" 
                                className="btn btn-secondary px-5 ms-2" 
                                onClick={() => navigate("/view-groups")}
                            >
                                View Groups
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CreateGroup;